import React, { useContext, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';

import { UiContext } from '../../context/UiContext';
import { hasAuthData } from '../../helpers/authStorage';
import UserService from '../../services/UserService';
import TrialExpiredModal from '../../modals/TrialExpiredModal';
import { useUiStore } from '../../store';
import useUIHelperContext from '../../hooks/useUIHelperContext';
import { USER_ROLES } from '../../constants/main';

export default function PrivateRoute({ children }) {
  const isAuthenticated = hasAuthData();
  const location = useLocation();
  const navigate = useNavigate();

  const { setRedirectAfterLogin } = useContext(UiContext);
  const { hideAllHelpers } = useUIHelperContext();
  const isTrialPaywallOpen = useUiStore((state) => state.isTrialPaywallOpen);
  const setTrialPaywallOpen = useUiStore((state) => state.setTrialPaywallOpen);

  useEffect(() => {
    if (!isAuthenticated) {
      setRedirectAfterLogin(location.pathname + location.search);
    }
  }, [isAuthenticated, location]);


  const localOnboardingOverride =
    typeof window !== 'undefined' &&
    sessionStorage.getItem('onboardingCompleted') === 'true';

  const { data: meData } = useQuery('me', UserService.getMe, {
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });

  const isTrialPaywalled = meData?.organizationBilling?.isPaywalled === true || isTrialPaywallOpen;

  useEffect(() => {
    if (!meData) return;
    if (meData?.organizationBilling?.isPaywalled === false && isTrialPaywallOpen) {
      setTrialPaywallOpen(false);
    }
  }, [isTrialPaywallOpen, meData, setTrialPaywallOpen]);

  useEffect(() => {
    if (!meData) return;

    // Super admins should never see onboarding
    if (meData.role === USER_ROLES.SUPER_ADMIN) return;

    const orgMeta = meData.Organization?.metadata || {};
    const isOwner = meData.orgRole?.access === 'owner';
    const orgOnboardingDone = orgMeta.onboardingCompleted === true;

    // Check if user data is complete (regardless of org flag)
    const userDataComplete = meData?.name?.trim() &&
                            (meData?.companyName?.trim() || meData?.Organization?.name?.trim()) &&
                            meData?.title?.trim();

    // Don't redirect if user manually skipped or if already on onboarding page
    const shouldCheckOnboarding = location.pathname !== '/onboarding' && !localOnboardingOverride;

    const shouldRedirect = isOwner && shouldCheckOnboarding && (!orgOnboardingDone || !userDataComplete);

    if (shouldRedirect) {
      navigate('/onboarding', { replace: true });
    }
  }, [meData, navigate, location.pathname, localOnboardingOverride]);

  const shouldShowTrialPaywall = isTrialPaywalled && location.pathname !== '/onboarding';
  useEffect(() => {
    if (shouldShowTrialPaywall) {
      hideAllHelpers();
    }
  }, [hideAllHelpers, shouldShowTrialPaywall]);

  return isAuthenticated ? (
    <>
      {children}
      <TrialExpiredModal show={shouldShowTrialPaywall} me={meData} />
    </>
  ) : (
    <Navigate to="/sign-in" />
  );
}
